import React, { useContext, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiCheckboxMultipleOutline, mdiPlus, mdiSwapVertical, mdiUnfoldLessHorizontal, mdiUnfoldMoreHorizontal } from "@mdi/js";
import commonUtils from "../../../../script/utils/commonUtils";
import antennaUtils from "../../../../script/utils/antennaUtils";

import formatRest from "../../../../rest/formatRest";
import antennaRest from "../../../../rest/antennaRest";

import { useInput } from "../../../hooks/useInput";
import { useValidation } from "../../../hooks/useValidation";
import { EntryContext } from "../../../contexts/EntryContext";

import { SectorItem } from "./SectorItem";
import { Grid } from "../../../components/Grid";
import { Button } from "../../../components/Button";
import { Spacer } from "../../../components/Spacer";
import { Tooltip } from "../../../components/Tooltip";
import { useOnChange } from "../../../hooks/useOnChange";
import { InputText } from "../../../components/InputText";
import { InputTextArea } from "../../../components/InputTextArea";
import { FileSelect } from "../../../components/FileSelect";
import { SortableList } from "../../../components/SortableList";

const TabStyle = styled.div`
    padding: 20px 0;
    border-radius: 0px 0px 5px 5px;
    border-top: 3px solid ${props => props.theme.greyLight};
`;

const HeaderStyle = styled.div`
    min-height: 60px;
    margin-bottom: 20px;
`;

const ToolbarStyle = styled.div`
    display: flex;
    margin-bottom: 16px;
    justify-content: space-between;
    >div {
        display: flex;
    }
`;

export const RadiosTab = () => {

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const [uploading, setUploading] = useState(false);
    const [toggleEnable, setToggleEnable] = useState(true);
    const [toggleDowntilt, setToggleDowntilt] = useState("max");
    const [antennaInfo, setAntennaInfo] = useState([]);
    const [expandedSectors, setExpandedSectors] = useState({});

    const siteNum = useInput("siteNum", entryContext.entry.siteNum);
    const entryName = useInput("name", entryContext.entry.name);
    const comments = useInput("comments", entryContext.entry.comments ?? "");

    const validationOptions = useMemo(() => ({ globalErrorMessage: intl.get("entry.tabs.radios") }), []);
    useValidation(value => !commonUtils.isBlank(value), [siteNum], validationOptions);
    useValidation(value => !commonUtils.isBlank(value), [entryName], validationOptions);

    useOnChange((name, value) => {
        entry[name] = value;
        entryContext.setEntry(entry);
    }, [siteNum, entryName, comments]);

    useEffect(() => {
        const antInfoArray = [];
        entryContext.entry.sectors.forEach(sector => {
            if (sector.antennaManufacturer !== null && sector.antennaName !== null) {
                const match = antInfoArray.find(info => info.manufacturer === sector.antennaManufacturer && info.name === sector.antennaName);
                if (!match) {
                    antInfoArray.push({ manufacturer: sector.antennaManufacturer, name: sector.antennaName, operator: sector.operator });
                }
            }
        });
        (async () => {
            const antennaInfo = await antennaRest.getAntennaInfo(antInfoArray);
            setAntennaInfo(antennaInfo);
        })();
    }, [entryContext.entry.sectors]);

    const onKeyDown = event => {
        if ("<>:\"/\\|?*.".includes(event.key)) {
            event.preventDefault();
        }
    };

    const onMerge = async event => {
        const file = await commonUtils.readFile(event);
        const { result, name } = file;
        try {
            const columnOffset = antennaUtils.nextColumn(entry);
            const entryToMerge = await formatRest.parseFormat(name, result);
            entryToMerge.sectors.forEach(sector => {
                sector.radios.forEach(radio => (radio.column += columnOffset));
                entry.sectors.push(sector);
            });
            entryContext.setEntry(entry);
        } finally {
            setUploading(false);
        }
    };

    const onAddSector = () => {
        entry.sectors.push({
            radios: [{ enabled: true, column: antennaUtils.nextColumn(entry) }],
        });
        entryContext.setEntry(entry);
    };

    const onToggleEnable = () => {
        const value = !toggleEnable;
        antennaUtils.loadSectorRadios(entry).forEach(({ radio }) => (radio.enabled = value));
        setToggleEnable(value);
        entryContext.setEntry(entry);
    };

    const onToggleDowntilt = async () => {
        const value = toggleDowntilt === "max" ? "min" : "max";
        const rows = antennaUtils.loadSectorRadios(entry);
        const antennaPortRefs = rows.map(({ sector, radio }) => ({
            antennaName: sector.antennaName,
            antennaFrequency: radio.antennaFrequency,
            antennaPort: radio.antennaPort,
        }));
        let downtilts = [];
        if (value === "max") {
            downtilts = await antennaRest.getMaxElecDowntilts(antennaPortRefs);
        } else {
            downtilts = await antennaRest.getMinElecDowntilts(antennaPortRefs);
        }
        rows.forEach(({ radio }, index) => {
            radio.antennaElecDowntilt = downtilts[index];
        });
        setToggleDowntilt(value);
        entryContext.setEntry(entry);
    };

    // new sectors start expanded, loaded ones collapsed
    const isExpanded = sector => expandedSectors[antennaUtils.itemKey(sector)] ?? !sector.id;

    const onChangeExpanded = (sector, value) => {
        setExpandedSectors(previous => ({ ...previous, [antennaUtils.itemKey(sector)]: value }));
    };

    const onChangeAllExpanded = value => {
        setExpandedSectors(Object.fromEntries(entry.sectors.map(sector => [antennaUtils.itemKey(sector), value])));
    };

    // the sector order is persisted by position, the radio columns stay untouched
    const onChangeOrder = (fromIndex, toIndex) => {
        const [sector] = entry.sectors.splice(fromIndex, 1);
        entry.sectors.splice(toIndex, 0, sector);
        entryContext.setEntry(entry);
    };

    return (
        <TabStyle>
            <HeaderStyle>
                <Grid template="85px 300px 400px">
                    <InputText {...siteNum.bind}
                        title={intl.get("entry.siteNum")}
                        maxLength="15"
                        onKeyDown={event => onKeyDown(event)} />
                    <InputText {...entryName.bind}
                        title={intl.get("entry.name")}
                        maxLength="90"
                        onKeyDown={event => onKeyDown(event)} />
                    <InputTextArea {...comments.bind}
                        rows="1"
                        maxLength="4000"
                        title={intl.get("entry.comments")} />
                </Grid>
            </HeaderStyle>
            <ToolbarStyle>
                <div>
                    <Tooltip message={intl.get("entry.radio.addAntenna")}>
                        <Button
                            compact
                            id="antennasAddRadioButton"
                            svgIcon={mdiPlus}
                            onClick={() => onAddSector()} />
                    </Tooltip>
                    <Tooltip message={intl.get("entry.expandAll")}>
                        <Button
                            compact
                            svgIcon={mdiUnfoldMoreHorizontal}
                            onClick={() => onChangeAllExpanded(true)} />
                    </Tooltip>
                    <Tooltip message={intl.get("entry.collapseAll")}>
                        <Button
                            compact
                            svgIcon={mdiUnfoldLessHorizontal}
                            onClick={() => onChangeAllExpanded(false)} />
                    </Tooltip>
                </div>
                <div>
                    <FileSelect
                        accept=".wwmobil, .bisx, .xml, .json"
                        value={intl.get("entry.merge")}
                        loading={uploading}
                        onSelect={event => onMerge(event)} />
                    <Spacer variant="vertical" />
                    <Button
                        svgIcon={mdiCheckboxMultipleOutline}
                        onClick={onToggleEnable}
                        value={toggleEnable === true ? intl.get("entry.deactivateAll") : intl.get("entry.activateAll")} />
                    <Spacer variant="vertical" />
                    <Button
                        svgIcon={mdiSwapVertical}
                        onClick={onToggleDowntilt}
                        value={toggleDowntilt === "max" ? intl.get("entry.setMinDowntilts") : intl.get("entry.setMaxDowntilts")} />
                </div>
            </ToolbarStyle>
            <SortableList onChangeOrder={onChangeOrder}>
                {entryContext.entry.sectors.map(sector =>
                    <SectorItem
                        key={antennaUtils.itemKey(sector)}
                        sector={sector}
                        expanded={isExpanded(sector)}
                        onChangeExpanded={value => onChangeExpanded(sector, value)}
                        antennaInfo={antennaInfo}
                        minMaxDowntilt={toggleDowntilt} />
                )}
            </SortableList>
        </TabStyle>
    );
};